import { createStyles, WithStyles, withStyles } from "@material-ui/core";
import * as React from "react";

import { RefinedByListItem } from ".";
import { camelize, Facet, Link, RelodeTheme, Text } from "../";
import { IFacets } from "../facet";

const styles = (theme: RelodeTheme) =>
    createStyles({
        root: {},
        refinedBy: {
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(4),
        },
        facets: {
            marginTop: theme.spacing(2),
        },
        undoAll: {
            fontSize: "14px",
        },
    });

export interface SearchFacetsProps {
    id?: string;
    facets?: IFacets;
    criteria?: any;
    facetConfig?: ISearchFacetConfig;
    hasResults: boolean;
    className?: string;
    refined?: boolean;
    showFiltersTitle?: boolean;
}

type OwnProps = SearchFacetsProps & WithStyles<typeof styles>;

export interface ISearchFacetConfig {
    handleMultipleSelection?: (
        idProp: string,
        nameProp: string,
        itemName?: string
    ) => () => void;
    handleSingleSelection?: (idProp: string, nameProp: string) => () => void;
    facets: Array<{
        id: string;
        title: string;
        idPropName: string;
        namePropName: string;
        isMultiple: boolean;
    }>;
}

export const SearchFacets = withStyles(styles, { name: "SearchResults" })(
    (props: OwnProps) => {
        const {
            id,
            facetConfig,
            facets: facetsProp,
            hasResults,
            className: classNameProp,
            criteria,
            classes,
            refined,
            showFiltersTitle,
        } = props;

        if (!facetsProp || !facetConfig) {
            return null;
        }

        const renderRefined = () => {
            const refinedFacets: any[] = [];
            return facetConfig.facets.reduce((acc: any[], facet) => {
                const facetNames = criteria[facet.namePropName];
                if (facetNames) {
                    if (!facet.isMultiple) {
                        acc.push(
                            <RefinedByListItem
                                key={facet.id}
                                filterName={facetNames}
                                handleFilter={
                                    facetConfig.handleSingleSelection &&
                                    facetConfig.handleSingleSelection(
                                        facet.idPropName,
                                        facet.namePropName
                                    )
                                }
                            />
                        );
                        return acc;
                    }

                    facetNames.map(facetName => {
                        if (refinedFacets.indexOf(facetName) === -1) {
                            acc.push(
                                <RefinedByListItem
                                    key={facetName}
                                    filterName={facetName}
                                    handleFilter={
                                        facetConfig.handleMultipleSelection &&
                                        facetConfig.handleMultipleSelection(
                                            facet.idPropName,
                                            facet.namePropName,
                                            facetName
                                        )
                                    }
                                />
                            );
                            refinedFacets.push(facetName);
                        }
                    });
                }
                return acc;
            }, []);
        };

        const undoAllRefined = () => {
            facetConfig.facets.map(facet => {
                const facetNames = criteria[facet.namePropName];
                if (facet.isMultiple && facetNames) {
                    facetNames.map(facetName => {
                        const handler = facetConfig.handleMultipleSelection(
                            facet.idPropName,
                            facet.namePropName,
                            facetName
                        );
                        handler();
                    });
                } else {
                    const handler =
                        facetConfig.handleSingleSelection &&
                        facetConfig.handleSingleSelection(
                            facet.idPropName,
                            facet.namePropName
                        );
                    handler();
                }
            });
        };

        const renderFacets = () =>
            facetConfig.facets.map(facet => {
                const facets = facetsProp[facet.id];
                const handler = facet.isMultiple
                    ? facetConfig.handleMultipleSelection &&
                      facetConfig.handleMultipleSelection(
                          facet.idPropName,
                          facet.namePropName
                      )
                    : facetConfig.handleSingleSelection &&
                      facetConfig.handleSingleSelection(
                          facet.idPropName,
                          facet.namePropName
                      );

                if (facets && facets.length) {
                    return (
                        <Facet
                            id={camelize(facet.title)}
                            key={facet.id}
                            title={facet.title}
                            facets={facets}
                            onFilter={handler}
                        />
                    );
                }
                return null;
            });

        const refinements = renderRefined();

        return (
            <div className={classNameProp}>
                {refined || (!!refinements && refinements.length > 0) ? (
                    <div
                        className={classes.refinedBy}
                        data-cy="search-refinedBy"
                        id={`${id}RefinedBy`}
                    >
                        <>
                            <Text variant="h3" gutterBottom isInline={true}>
                                you refined by
                            </Text>
                            {refinements.length > 1 ? (
                                <>
                                    {" "}
                                    (
                                    <Link
                                        id={`${id}RefinedByUndoAll`}
                                        className={classes.undoAll}
                                        highlightType="internal"
                                        gutterBottom={false}
                                        onClick={undoAllRefined}
                                        data-cy="search-undoAll-link"
                                    >
                                        undo all
                                    </Link>
                                    )
                                </>
                            ) : null}
                        </>
                        {refinements}
                    </div>
                ) : null}

                {!!hasResults && (
                    <div className={classes.facets} id={`${id}Filters`}>
                        {showFiltersTitle ? (
                            <Text variant="h3" gutterBottom>
                                filters
                            </Text>
                        ) : null}
                        {renderFacets()}
                    </div>
                )}
            </div>
        );
    }
);

SearchFacets.displayName = "SearchFacets";
